import type { ErrorEvent, EventHint } from "@sentry/nextjs";

export const SENTRY_CONFIG = {
	DSN: "https://d218b2f6bc3543a59ac21784d416b73b@o1354667.ingest.sentry.io/6724452",
	ENVIRONMENT: process.env.DEPLOY_ENV || "local",
	RELEASE: process.env.SENTRY_RELEASE || "dev",
	TRACES_SAMPLE_RATE: 0.0,
	TUNNEL_PATH: "/api/sentry",
} as const;

type BeforeSendFilter = (
	event: ErrorEvent,
	hint: EventHint
) => ErrorEvent | null;

const beforeSendFilters: BeforeSendFilter[] = [];

export function addBeforeSendFilter(filter: BeforeSendFilter) {
	beforeSendFilters.push(filter);
}

function beforeSend(event: ErrorEvent, hint: EventHint): ErrorEvent | null {
	return beforeSendFilters.reduce<ErrorEvent | null>(
		(e, filter) => (e ? filter(e, hint) : e),
		event
	);
}

function filterEvents(event: ErrorEvent, hint: EventHint): ErrorEvent | null {
	const originalException = hint?.originalException as
		| { message?: string }
		| undefined;
	const message = event?.message || originalException?.message;

	const filteredPhrases = ["Failed to fetch", "The operation was aborted"];

	if (message && filteredPhrases.some((phrase) => message.includes(phrase))) {
		console.warn(`[Sentry] Dropping event with message: "${message}"`);
		return null;
	}

	return event;
}

function normalizeUrl(url: string): string {
	try {
		const parsed = new URL(url);
		const segments = parsed.pathname.split("/").map((seg) => {
			if (!seg) return seg;
			if (/^\d+$/.test(seg)) return ":id";
			if (
				/^[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}$/.test(
					seg
				)
			)
				return ":id";
			return seg;
		});

		return segments.join("/") || "/";
	} catch {
		return url;
	}
}

function normalizeEvent(event: ErrorEvent, hint: EventHint): ErrorEvent {
	const { request } = event;
	const originalException = hint?.originalException as
		| { status?: number }
		| undefined;
	const statusCode =
		event.contexts?.response?.status_code || originalException?.status;
	if (statusCode && request?.url) {
		event.fingerprint = [
			`status:${statusCode}`,
			`page:${normalizeUrl(request.url)}`,
			"{{ default }}",
		];
	}
	return event;
}

addBeforeSendFilter(filterEvents);
addBeforeSendFilter(normalizeEvent);

export function getSentryConfig() {
	return {
		dsn: SENTRY_CONFIG.DSN,
		environment: SENTRY_CONFIG.ENVIRONMENT,
		release: SENTRY_CONFIG.RELEASE,
		tracesSampleRate: SENTRY_CONFIG.TRACES_SAMPLE_RATE,
		tunnel:
			typeof window === "undefined"
				? undefined
				: window.location.origin + SENTRY_CONFIG.TUNNEL_PATH,
		beforeSend,
	};
}
